
import { Translations, Product, CaseStudy, Resource, Testimonial, Partner, SEOSettings, BlogPost, FaqItem, Certificate, Badge, IndustryCategory } from '../types';

/**
 * 💡 MEITAI 工业云同步服务 - 增强同步可靠版
 */

const getEnv = (key: string): string => {
    try {
        if (key === 'CUSTOM_API_URL') return process.env.CUSTOM_API_URL || '';
        const val = (import.meta as any).env?.[`VITE_${key}`];
        return val || '';
    } catch { return ''; }
};

const DEFAULT_API_DOMAIN = "api.meitai.us.kg";
const DATA_KEY = 'meitai_site_data';
const PERSISTENT_URL_KEY = 'meitai_manual_api_url';

export const getBaseUrl = () => {
    const manual = localStorage.getItem(PERSISTENT_URL_KEY);
    if (manual) return manual.replace(/\/+$/, '');
    
    const envUrl = getEnv('CUSTOM_API_URL') || `https://${DEFAULT_API_DOMAIN}/api`;
    return envUrl.replace(/\/+$/, '');
};

export interface SiteData {
  translations: Translations;
  products: Product[];
  cases: CaseStudy[];
  resources: Resource[];
  testimonials?: Testimonial[];
  partners?: Partner[];
  seoSettings?: SEOSettings;
  hiddenNavItems?: string[];
  blogPosts?: BlogPost[];
  faqItems?: FaqItem[];
  certificates?: Certificate[];
  badges?: Badge[];
  industryCategories?: IndustryCategory[];
  lastUpdated: string;
}

const getTestPaths = () => {
    const baseUrl = getBaseUrl();
    if (baseUrl.toLowerCase().endsWith('/api')) {
        return [`${baseUrl}/data`];
    }
    return [`${baseUrl}/api/data`];
};

/**
 * 核心逻辑：自动解包后端返回的数据
 */
const unwrapData = (body: any): SiteData | null => {
    if (!body) return null;
    
    // 情况 A: 数据直接在根部 (标准 JSON 文件)
    if (body.products || body.translations) return body as SiteData;
    
    // 情况 B: 数据被包装在 value 字段中 (KV 存储系统常见)
    if (body.value && (body.value.products || body.value.translations)) {
        return body.value as SiteData;
    }
    
    return null;
};

const fetchDetailed = async (url: string, options: RequestInit = {}) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 10000);

    try {
        const response = await fetch(url, { 
            ...options, 
            signal: controller.signal,
            cache: 'no-cache',
            mode: 'cors',
            headers: {
                ...options.headers,
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
            }
        });
        clearTimeout(id);
        
        const isJson = response.headers.get('content-type')?.includes('application/json');
        let body = null;
        try { if (isJson) body = await response.json(); } catch (e) {
            // Ignore JSON parse errors
        }

        if (response.ok && body) {
            const data = unwrapData(body);
            if (data) {
                return { success: true, isOnline: true, data, url, status: response.status };
            }
        }

        if (response.status === 404) {
            return { success: false, isOnline: true, error: 'Empty (404)', url, status: 404 };
        }

        return { success: false, isOnline: true, error: `Invalid Format or HTTP ${response.status}`, url, status: response.status };

    } catch (e: any) {
        clearTimeout(id);
        return { success: false, isOnline: false, error: e.message, url, status: 0 };
    }
};

export const fetchRemoteData = async (): Promise<{data: SiteData | null, isOnline: boolean, error?: string}> => {
    const paths = getTestPaths();
    // 探测所有路径，取第一个成功的
    const results = await Promise.all(paths.map(url => fetchDetailed(`${url}?key=${DATA_KEY}&t=${Date.now()}`)));
    
    const winner = results.find(r => r.success);
    const anyOnline = results.find(r => r.isOnline);

    if (winner) {
        return { data: winner.data as SiteData, isOnline: true };
    }

    if (anyOnline) {
        return { data: null, isOnline: true, error: anyOnline.error };
    }

    return { data: null, isOnline: false, error: results.map(r => r.error).join(', ') };
};

export const saveRemoteData = async (data: SiteData): Promise<boolean> => {
    const paths = getTestPaths();
    const dataToSave = { ...data, lastUpdated: new Date().toISOString() };
    
    const results = await Promise.all(paths.map(async (url) => {
        try {
            const response = await fetch(`${url}?key=${DATA_KEY}`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ 
                    key: DATA_KEY, 
                    value: dataToSave 
                }),
                mode: 'cors'
            });
            
            if (response.ok) {
                console.log(`%c[MEITAI CLOUD]%c Successfully uploaded ${JSON.stringify(dataToSave).length} bytes to ${url}`, "color: #0c87eb; font-weight: bold", "color: #555");
                return true;
            }
            return false;
        } catch (e) {
            console.error(`[MEITAI CLOUD] Save failed for ${url}:`, e);
            return false;
        }
    }));

    return results.some(r => r === true);
};

export const setManualApiUrl = (url: string) => {
    if (!url) {
        localStorage.removeItem(PERSISTENT_URL_KEY);
    } else {
        let cleanUrl = url.trim().replace(/\/+$/, '');
        if (!cleanUrl.startsWith('http')) {
            cleanUrl = `${window.location.protocol}//${cleanUrl}`;
        }
        localStorage.setItem(PERSISTENT_URL_KEY, cleanUrl);
    }
    window.location.reload();
};

export const isApiConfigured = () => true;
export const getStorageType = () => {
    const current = getBaseUrl();
    if (current.includes(DEFAULT_API_DOMAIN)) return 'MEITAI CLOUD';
    return 'CUSTOM SERVER';
};
